import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ShimmerCard from '@nykaa/cart-shared/components/Shimmer/Card';
import ProductList from '@nykaa/cart-shared/wrapper/ProductList';
import { useIsLoggedIn } from 'hooks/auth';
import { deviceTypes, ORIGIN_SOURCE } from '@nykaa/cart-shared/constants';
import { CartEvents } from '@nykaa/cart-shared/constants/tracking';
import { setCartInfo, setToastMessage } from '@nykaa/cart-shared/store/action';
import { EDD_EXPERIEMENT_HEADER } from '@nykaa/cart-shared/store/constants';
import {
  addToWishlist,
  removeProductFromBag,
  updateProductQuantity,
} from '@nykaa/cart-shared/store/service';
import { getAnalyticsData } from '@nykaa/cart-shared/helpers/tracking';
import { ItemObjectType } from '@nykaa/cart-shared/store/types/items';
import { IUpdateProductQuantityParams } from '@nykaa/cart-shared/store/types/requestParams';
import { pushData } from '@nykaa/data-layer/utils';
import { State } from 'store/types';
import { reactStorageKeys } from 'constants/auth';
import { localStorageService } from 'service/storage';
import { logger } from 'logging';
import { storeAuthInStorage } from 'store/auth/helper';

export const setItemQuantityInStorage = (value: number) => {
  try {
    const reactConfigData = localStorageService.getItem(
      reactStorageKeys.CONFIG
    );
    if (reactConfigData) {
      const parsedReactConfigData = JSON.parse(reactConfigData);
      if (
        parsedReactConfigData?.data?.result?.cartItemsTotal ||
        parsedReactConfigData?.data?.result?.cartItemsTotal == 0
      ) {
        parsedReactConfigData.data.result.cartItemsTotal = value;
        storeAuthInStorage(parsedReactConfigData);
      }
    }
  } catch (err) {
    logger.error(err as Error, 'Error Updating Quantity in storage');
  }
};

interface Props {
  isConvenienceFeeEnabled?: boolean;
  getQuantAnnounceText?: (text: string) => void;
}

const Products = ({ isConvenienceFeeEnabled, getQuantAnnounceText }: Props) => {
  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const cartInfo = useSelector((state: State) => state.cartReducer.cartInfo);
  const {
    cartEddWidget,
    cartFreeSampleNewDesignEnable,
    cartViewOffersForItem,
    cartExclusionMessageForOffers,
    cartItemDiscount,
  } = useSelector((state: State) => state.remoteConfig.switches) || {};
  const cartFreeGiftBanner =
    useSelector(
      (state: State) => state.remoteConfig.configs.cartFreeGiftBanner
    ) || {};
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectWishlist, setSelectWishlist] = useState(false);

  const { pincode, items } = cartInfo || {};

  const handleRemoveItem = async ({
    item,
  }: {
    item?: ItemObjectType;
    moveItemToHistory?: boolean;
    removeAll?: boolean;
  }) => {
    const productId = item?.productId || '';
    const params = {
      deviceType: deviceTypes.DESKTOP,
      productIds: [productId],
      originSource: ORIGIN_SOURCE.CART,
      pincode,
      headers: { [EDD_EXPERIEMENT_HEADER]: cartEddWidget },
      domain: __PLATFORM__,
      catalog_tag_filter: __PLATFORM__,
    };
    setItemsLoading(true);
    try {
      const data = await removeProductFromBag(params, isConvenienceFeeEnabled);
      if (!selectWishlist) {
        const payload = getAnalyticsData(CartEvents.CART_ITEM_REMOVE, {
          ...item,
          cartInfo: data,
        });
        const removeFromCartPayload = getAnalyticsData(
          CartEvents.REMOVE_FROM_CART_GTM,
          {
            ...item,
            cartInfo: data,
          }
        );
        pushData(payload.gtmData);
        pushData(removeFromCartPayload.gtmData);
      }
      setItemQuantityInStorage(data?.quantity);
      setCartInfo(data)(dispatch);
    } catch (err: any) {
      setToastMessage(err?.message || 'Something went wrong!')(dispatch);
    } finally {
      setItemsLoading(false);
      setSelectWishlist(false);
    }
  };

  const updateItem = async ({
    item,
    qty,
  }: {
    item: ItemObjectType;
    qty: number;
  }) => {
    const productId = item?.productId || '';
    const params: IUpdateProductQuantityParams = {
      deviceType: deviceTypes.DESKTOP,
      is_guest: !isLoggedIn ? 1 : 0,
      pincode,
      items: [
        {
          qty,
          productId: productId,
        },
      ],
      originSource: ORIGIN_SOURCE.CART,
      headers: { [EDD_EXPERIEMENT_HEADER]: cartEddWidget },
      domain: __PLATFORM__,
      catalog_tag_filter: __PLATFORM__,
    };
    setItemsLoading(true);
    try {
      const data = await updateProductQuantity(params, isConvenienceFeeEnabled);
      setItemQuantityInStorage(data?.quantity);
      setCartInfo(data)(dispatch);
      const trackingPayload = getAnalyticsData(CartEvents.QUANTITY_UPDATE_GTM, {
        item: { ...item, quantity: qty },
        oldQty: item.quantity,
        cartInfo: data,
      });
      pushData(trackingPayload.gtmData);
    } catch (error: any) {
      setToastMessage(error?.message || 'Something went wrong!')(dispatch);
    } finally {
      setItemsLoading(false);
    }
  };

  const addItemToWishlist = async (item: ItemObjectType) => {
    const productId = item?.productId || '';
    const params = {
      deviceType: deviceTypes.DESKTOP,
      host: ORIGIN_SOURCE.CART,
      items: [productId],
      originSource: ORIGIN_SOURCE.CART,
      headers: { [EDD_EXPERIEMENT_HEADER]: cartEddWidget },
      domain: __PLATFORM__,
      catalog_tag_filter: __PLATFORM__,
    };
    try {
      setSelectWishlist(true);
      const data = await addToWishlist(params);
      const payload = getAnalyticsData(CartEvents.CART_ITEM_ADD_TO_WISHLIST, {
        ...item,
        cartInfo: data,
      });
      pushData(payload.gtmData);
      handleRemoveItem({ item });
    } catch (err: any) {
      setToastMessage(err?.message || 'Something went wrong!')(dispatch);
    }
  };

  const handleTracking = ({ type, data }: { type: CartEvents; data: any }) => {
    const payload = getAnalyticsData(type, data);
    pushData(payload.gtmData);
  };

  const customCallbacks = {
    updateItem,
    removeItem: handleRemoveItem,
    addToWishlist: addItemToWishlist,
    setToastMessage: (message: string) => {
      if (message) {
        setToastMessage(message)(dispatch);
      }
    },
    handleTracking,
    handleRedirection: (url: string) => {
      window.location.href = url;
    },
  };

  const ItemShimmer = () => {
    const shimmer = Array.from({ length: items?.length }, (_, index) => index);
    return (
      <>
        {shimmer.map((key) => (
          <ShimmerCard key={key} />
        ))}
      </>
    );
  };

  if (itemsLoading) {
    return <ItemShimmer />;
  }

  return (
    <ProductList
      cartInfo={cartInfo}
      isGuest={!isLoggedIn}
      deviceType={deviceTypes.DESKTOP}
      callbacks={customCallbacks}
      cartFreeGiftBanner={cartFreeGiftBanner}
      getQuantAnnounceText={getQuantAnnounceText}
      experiments={{
        cartFreeSampleNewDesignEnable,
        cartViewOffersForItem,
        cartExclusionMessageForOffers,
        cartItemDiscount,
      }}
      ariaLevel={2}
    />
  );
};

export default Products;
